import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Cookie } from "lucide-react";

const UNITS = ["pcs", "kg", "g", "litre", "ml", "packet", "bottle", "box", "dozen"];
const CATEGORIES = ["Bakery", "Confectionery", "Kitchen prep", "Frozen", "Beverage", "Other"];
const EMPTY = { name: "", category: "Bakery", unit: "pcs", default_rate: 0, notes: "", active: true };

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function ProductsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/finished-products");
      setItems(data);
    } catch { toast.error("Could not load products"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Product name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, default_rate: Number(form.default_rate) || 0 };
      if (editing === "new") {
        const { data } = await api.post("/staff/finished-products", payload);
        setItems((p) => [...p, data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success(`Added ${data.name}`);
      } else {
        const { data } = await api.patch(`/staff/finished-products/${editing.id}`, payload);
        setItems((p) => p.map((x) => (x.id === data.id ? data : x)));
        toast.success(`Updated ${data.name}`);
      }
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Deactivate product "${p.name}"?`)) return;
    try {
      await api.delete(`/staff/finished-products/${p.id}`);
      setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, active: false } : x)));
      toast.success("Deactivated");
    } catch { toast.error("Delete failed"); }
  };

  const filtered = showInactive ? items : items.filter((x) => x.active);

  return (
    <div data-testid="products-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <label className="inline-flex items-center gap-2 text-sm">
          <input data-testid="products-show-inactive" type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        <button data-testid="product-new" onClick={openNew} className="btn-brass !py-2 !px-4 text-sm">
          <Plus size={14} /> New product
        </button>
      </div>

      <div className="mt-6 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
        {loading && <div className="p-6 text-[hsl(var(--ink-soft))]">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div data-testid="products-empty" className="p-10 text-center text-[hsl(var(--ink-soft))]">
            <Cookie className="mx-auto mb-2 opacity-40" size={28} />
            No finished products yet — add your first one (Croissants, Cakes, etc.).
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <ul className="divide-y divide-[hsl(var(--rule))]">
            {filtered.map((p) => (
              <li key={p.id} data-testid={`product-${p.id}`} className="p-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
                <div>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <div className="text-base font-semibold">{p.name}</div>
                    {p.category && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[hsl(var(--brass)/0.15)] text-[hsl(var(--brass-2))]">{p.category}</span>
                    )}
                    <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">Unit: {p.unit}</span>
                    {!p.active && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]">Inactive</span>}
                  </div>
                  <div className="mt-1 text-sm">
                    Default rate: <span className="font-semibold">{inr(p.default_rate)}</span> / {p.unit}
                  </div>
                  {p.notes && <div className="mt-0.5 text-xs text-[hsl(var(--ink-soft))]">{p.notes}</div>}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button data-testid={`product-edit-${p.id}`} onClick={() => openEdit(p)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg))]">
                    <Pencil size={12} /> Edit
                  </button>
                  {p.active && (
                    <button data-testid={`product-del-${p.id}`} onClick={() => remove(p)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]">
                      <Trash2 size={12} /> Deactivate
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <div data-testid="product-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
          <form onSubmit={save} className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-lg p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="eyebrow">{editing === "new" ? "New product" : "Edit product"}</div>
                <h2 className="mt-1 text-2xl">{form.name || "—"}</h2>
              </div>
              <button type="button" onClick={close} className="text-[hsl(var(--ink-soft))]"><X size={18} /></button>
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Name *
                <input required data-testid="product-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="tbb-inp mt-1 w-full" autoFocus />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Category
                <select data-testid="product-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="tbb-inp mt-1 w-full">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Unit
                <select data-testid="product-form-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="tbb-inp mt-1 w-full">
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </label>
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Default rate (₹ per {form.unit})
                <input type="number" min="0" step="0.01" data-testid="product-form-rate" value={form.default_rate} onChange={(e) => setForm({ ...form, default_rate: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Notes
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="tbb-inp mt-1 w-full resize-none" />
              </label>
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={close} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
              <button type="submit" data-testid="product-save" disabled={saving} className="btn-brass !py-2.5 !px-5 text-sm">{saving ? "Saving..." : "Save product"}</button>
            </div>
            <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
          </form>
        </div>
      )}
    </div>
  );
}
